/**
 * Pantalla común de "enlace inválido" (T033): la muestran `/activar/{token}`, `/instalar` y
 * `/registrar` cuando la API responde `410` (`INVITATION_INVALID` o `INVITATION_EXPIRED`, mismo
 * mensaje neutro por diseño) o cuando ya no hay activación en curso en `sessionStorage`.
 */
import { Link } from '@tanstack/react-router';

import { ActivationInvalidReasonSchema, type ErrorCode } from '@fermin/contracts';

import { ApiError } from '../../api/client';
import { Button, buttonClasses } from '../../components/ui/button';
import { Screen } from '../../components/ui/screen';
import { SpinnerRing } from '../../components/ui/spinner-ring';
import { t } from '../../i18n';

/** Códigos que significan "esta invitación ya no sirve" (wiring §3.2 y §4.2). */
export const INVITATION_GONE_CODES: readonly ErrorCode[] = [
  'INVITATION_INVALID',
  'INVITATION_EXPIRED',
];

export function isInvitationGone(error: unknown): boolean {
  return error instanceof ApiError && INVITATION_GONE_CODES.includes(error.code);
}

/**
 * 005 · QA-9: el enlace o el código dejaron de servir porque el administrador generó una invitación
 * **nueva** mientras la persona seguía registrándose (`details.reason`, wiring de la 002 §12.3). Distingue este
 * caso de "enlace inventado o vencido" sin revelar nada del trabajador: solo aplica cuando el
 * servidor ya lo marcó así.
 */
export function isInvitationSuperseded(error: unknown): boolean {
  if (!(error instanceof ApiError)) {
    return false;
  }
  if (error.code !== 'INVITATION_INVALID' && error.code !== 'HANDOFF_CODE_INVALID') {
    return false;
  }
  return error.details?.reason === ActivationInvalidReasonSchema.enum.INVITATION_SUPERSEDED;
}

export interface InvalidLinkScreenProps {
  /** Texto alternativo (p. ej. cuando no hay token guardado en vez de un 410). */
  description?: string;
}

export function InvalidLinkScreen({ description }: InvalidLinkScreenProps) {
  return (
    <Screen
      data-testid="activation-invalid"
      align="center"
      title={t('activation.invalid.title')}
      lead={description ?? t('activation.invalid.description')}
      footer={
        <Link to="/dispositivo-no-registrado" className={buttonClasses({ variant: 'secondary' })}>
          {t('activation.invalid.action')}
        </Link>
      }
    />
  );
}

/** Estado de carga de las pantallas de activación. */
export function ActivationLoadingScreen({ label }: { label?: string }) {
  const text = label ?? t('activation.welcome.loading');
  return (
    <Screen data-testid="activation-loading" align="center">
      <div className="flex flex-col items-center gap-fm-3">
        <SpinnerRing label={text} />
        <p aria-live="polite" className="text-fm-base text-fm-text-secondary">
          {text}
        </p>
      </div>
    </Screen>
  );
}

export interface ActivationErrorScreenProps {
  message?: string;
  onRetry: () => void;
}

/** Fallo no esperado (red, 5xx): se puede reintentar sin perder el enlace. */
export function ActivationErrorScreen({ message, onRetry }: ActivationErrorScreenProps) {
  return (
    <Screen
      data-testid="activation-error"
      align="center"
      title={t('activation.error.title')}
      lead={message ?? t('common.errors.NETWORK')}
      footer={<Button onClick={onRetry}>{t('common.buttons.retry')}</Button>}
    />
  );
}
